import { Injectable, inject } from '@angular/core';
import {
  SimpleXploreItemDTO,
  UgcNameCheckResponse,
  UgcPhotoUploadDTO
} from '../shared/object/xplorer-be-parsed-classes';
import { TokenStorageService } from './auth/token-storage.service';

export type UgcCreateDraftTargetSelection =
  | { mode: 'new' }
  | { mode: 'existing'; item: SimpleXploreItemDTO };

export interface UgcCreateDraftLocationSnapshot {
  latitude: number;
  longitude: number;
  accuracy: number | null;
  timestamp: number;
}

export interface UgcCreateWizardDraft {
  xploreName: string;
  nameCheckResponse: UgcNameCheckResponse | null;
  selectedTarget: UgcCreateDraftTargetSelection | null;
  capturedPhoto: UgcPhotoUploadDTO | null;
  currentLocation: UgcCreateDraftLocationSnapshot | null;
}

interface PersistedUgcCreateWizardDraft extends UgcCreateWizardDraft {
  version: 1;
  savedAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class UgcCreateDraftService {
  private static readonly STORAGE_PREFIX = 'ugc-create-draft';

  private readonly tokenStorage = inject(TokenStorageService);
  private readonly draftMemory = new Map<string, UgcCreateWizardDraft>();

  getDraft(territoryCode: string): UgcCreateWizardDraft | null {
    const storageKey = this.buildStorageKey(territoryCode);
    const memoryDraft = this.draftMemory.get(storageKey);
    if (memoryDraft) {
      return memoryDraft;
    }

    const persistedDraft = this.readPersistedDraft(storageKey);
    if (persistedDraft) {
      this.draftMemory.set(storageKey, persistedDraft);
    }

    return persistedDraft;
  }

  saveDraft(territoryCode: string, draft: UgcCreateWizardDraft): void {
    const storageKey = this.buildStorageKey(territoryCode);
    this.draftMemory.set(storageKey, draft);

    const persistedDraft: PersistedUgcCreateWizardDraft = {
      ...draft,
      version: 1,
      savedAt: Date.now()
    };

    this.persistDraft(storageKey, persistedDraft);
  }

  clearDraft(territoryCode: string): void {
    const storageKey = this.buildStorageKey(territoryCode);
    this.draftMemory.delete(storageKey);

    if (!this.isSessionStorageAvailable()) {
      return;
    }

    sessionStorage.removeItem(storageKey);
  }

  private persistDraft(storageKey: string, persistedDraft: PersistedUgcCreateWizardDraft): void {
    if (!this.isSessionStorageAvailable()) {
      return;
    }

    try {
      sessionStorage.setItem(storageKey, JSON.stringify(persistedDraft));
    } catch {
      const fallbackDraft: PersistedUgcCreateWizardDraft = {
        ...persistedDraft,
        capturedPhoto: null
      };

      try {
        sessionStorage.setItem(storageKey, JSON.stringify(fallbackDraft));
      } catch {
        sessionStorage.removeItem(storageKey);
      }
    }
  }

  private readPersistedDraft(storageKey: string): UgcCreateWizardDraft | null {
    if (!this.isSessionStorageAvailable()) {
      return null;
    }

    const rawDraft = sessionStorage.getItem(storageKey);
    if (!rawDraft) {
      return null;
    }

    try {
      const persistedDraft = JSON.parse(rawDraft) as Partial<PersistedUgcCreateWizardDraft>;
      if (persistedDraft.version !== 1) {
        sessionStorage.removeItem(storageKey);
        return null;
      }

      return {
        xploreName: persistedDraft.xploreName ?? '',
        nameCheckResponse: persistedDraft.nameCheckResponse ?? null,
        selectedTarget: persistedDraft.selectedTarget ?? null,
        capturedPhoto: persistedDraft.capturedPhoto ?? null,
        currentLocation: persistedDraft.currentLocation ?? null
      };
    } catch {
      sessionStorage.removeItem(storageKey);
      return null;
    }
  }

  private buildStorageKey(territoryCode: string): string {
    const normalizedTerritoryCode = territoryCode.trim().toLowerCase() || 'unknown';
    return `${UgcCreateDraftService.STORAGE_PREFIX}::${this.resolveScopeKey()}::${normalizedTerritoryCode}`;
  }

  private resolveScopeKey(): string {
    const user = this.tokenStorage.getUser();
    if (user?.keycloakId) {
      return `keycloak:${user.keycloakId}`;
    }

    if (user?.userId) {
      return `user:${user.userId}`;
    }

    const tokenPayload = this.tokenStorage.getTokenPayload(this.tokenStorage.getToken());
    if (tokenPayload?.sub) {
      return `subject:${tokenPayload.sub}`;
    }

    return 'guest';
  }

  private isSessionStorageAvailable(): boolean {
    return typeof sessionStorage !== 'undefined';
  }
}
